<template>
  <div class="w-base-level-table-look-a-like">
    <BaseLevelTableHeaderContainer
      :allow-redirect-to-search="allowRedirectToSearch"
      :allow-add="isAddable"
      :allow-change-table-headers="isHeaderChangeable"
      :allow-change-filters="allowChangeFilters"
      :allow-presets="allowPresets"
      :allow-column-mode-switch="allowColumnModeSwitch"
      :preset.sync="preset"
      :filter-card-state.sync="filterCardState"
      :concept="concept"
      :selected-rows.sync="selectedRows"
      :actions="actionArray"
      :headers="headers"
      :ordered-headers.sync="orderedHeaders"
      :not-visible-headers.sync="notVisibleHeaders"
      :ordered-filters.sync="orderedFilters"
      :not-visible-filters.sync="notVisibleFilters"
      :available-presets="availablePresets"
      :truncate-data.sync="truncateData"
      :truncate-options.sync="truncateOptions"
      :excel-export-loading="excelExportLoading"
      :csv-export-loading="csvExportLoading"
      :zip-export-loading="zipExportLoading"
      @toNavigate:clicked="redirectToLastHistoryObject"
      @new:clicked="invokeNewAction"
    >
      <template slot="searchFilter">
        <slot name="searchFilter" />
      </template>

      <template slot="inner-table-actions">
        <w-table-action-wrapper>
          <w-download-button @click="exportData">
            <template #tooltip>
              {{ $t('Components.sentences.excelExport') }}
            </template>
          </w-download-button>
        </w-table-action-wrapper>
        <slot name="inner-table-actions" />
      </template>
    </BaseLevelTableHeaderContainer>

    <v-expansion-panels v-model="panelCollapseOnXS">
      <v-expansion-panel>
        <w-expansion-panel-header class="w-expansion-panel-header">
          <span>{{ $t('Common.filter&GroupBy') }}</span>
        </w-expansion-panel-header>
        <w-expansion-panel-content class="w-expansion-panel-content">
          <BaseLevelFilterContainer
            :concept="concept"
            :does-table-exists="tableExists"
            :filter-card-state.sync="filterCardState"
            :filters="filters"
            :get-table-state="getCachedTable"
            :is-default.sync="presetIsDefault"
            :name.sync="presetName"
            :not-visible-filters.sync="notVisibleFilters"
            :ordered-filters.sync="orderedFilters"
            :preset.sync="preset"
            :save-columns.sync="presetSaveColumns"
            :set-preset-obj="setCachedPresetObj"
            @set:filter="invokeSetCachedFilterValue"
          >
            <template slot="prependSearchFilter">
              <slot name="prependSearchFilter" />
            </template>

            <template slot="searchFilter">
              <slot name="searchFilter" />
            </template>
          </BaseLevelFilterContainer>
        </w-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <BaseLevelTableContainer
      ref="baseLevelTableContainer"
      :filters="filters"
      :mandatory-filters="mandatoryFilters"
      :get-data="getData"
      :headers="headers"
      :initial-headers="headers"
      :hide-headers="hideHeaders"
      :invoked-param-handler="invokedParamHandler"
      :is-clickable="isClickable"
      :not-visible-headers="notVisibleHeaders"
      :number-of-entries-colspan="numberOfEntriesColspan"
      :with-total-hits="withTotalHits"
      :ordered-headers="orderedHeaders"
      :path="path"
      :search="search"
      :selected="selectedRows"
      :show-select="showSelect"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :with-default-footer="withDefaultFooter"
      :truncate-data="truncateData"
      :truncate-options="truncateOptions"
      :has-max-height="hasMaxHeight"
      toggle-force-reload
      @input="(newValue) => $emit('input', newValue)"
      @click:row="invokeHandleRowClick"
      @update:options="invokeSetCachedSort"
    >
      <template #top>
        <slot name="top" />
      </template>

      <template #top-title>
        {{ name }}
      </template>

      <template #table-top-actions>
        <slot name="table-top-actions" />
      </template>

      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          v-bind="slotData"
          :name="name"
        />
      </template>
    </BaseLevelTableContainer>

    <slot name="default" />
  </div>
</template>

<script>
// components
import BaseLevelFilterContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelFilterContainer';
import BaseLevelTableContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelTableContainer';
import BaseLevelTableHeaderContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelTableHeaderContainer';
// helpers
import tableStoreService from '@/components/base_components/tables/services/table-store-service';
// enums
import BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS from '@/components/view_root_components/base_level_table_module/enums/BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS';
import LOOK_A_LIKE_FEATURE_FLAG_PROPS from '@/components/global_components/tables/base-table-look-a-like/enums/LOOK_A_LIKE_FEATURE_FLAG_PROPS';
// mixins
import lookALikeGettersAndSetter from '@/components/global_components/tables/base-table-look-a-like/mixins/look-a-like-cache-getters-and-setters';
import baseLevelTableCacheHandlerMixin from '@/components/view_root_components/base_level_table_module/mixins/base-level-table-cache-handler-mixin';
import baseLevelTableMixin from '@/components/view_root_components/base_level_table_module/mixins/base-level-table-mixin';
import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';

/**
 * @component WorkBaseLevelTableLookALike
 * @description Full table component in "look-a-like" style for base-level data.
 * Combines table, filter and header with cache support and bulk actions.
 *
 * @prop {*} [value] - Table state (filters, sorting, etc.) via v-model.
 * @prop {String} concept - Table concept identifier for configuration. (Required)
 * @prop {Array} [selectedRows=[]] - Currently selected table rows.
 * @prop {Boolean} [allowDownload=true] - Show download button.
 *
 * @emits {Object} input - Emitted when the table state changes.
 */
export default {
  name: 'WorkBaseLevelTableLookALike',
  components: {
    BaseLevelTableContainer,
    BaseLevelFilterContainer,
    BaseLevelTableHeaderContainer,
  },
  props: {
    value: undefined,
    concept: { type: String, required: true },
    selectedRows: { type: Array, default: () => [] },
    allowDownload: { type: Boolean, default: true },
  },
  data() {
    return {
      panel: 0,
      fields: undefined,
    };
  },
  mixins: [
    baseLevelTableMixin(
      BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS,
      LOOK_A_LIKE_FEATURE_FLAG_PROPS,
      baseLevelTableCacheHandlerMixin(lookALikeGettersAndSetter)
    ),
  ],
  computed: {
    panelCollapseOnXS() {
      if (this.$vuetify.breakpoint.xsOnly)
        return;
      else
        return 0;
    },
  },
  watch: {
    value: {
      async handler(newValue) {
        if (newValue == undefined) {
          this.$emit(
            'input',
            tableStoreService.generateTableObject({
              [TABLE_DATA_KEYS.FILTERS]: this.initialFilter,
              [TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS]: this.getHiddenHeaders(),
            })
          );
          await this.loadPresetsAndSetDefault();
        }
      },
      immediate: true,
    },
    'filters.search'(newSearchValue) {
      this.invokeSetCachedSearch(newSearchValue);
    },
  },
};
</script>

<style lang='scss' scoped>
.w-base-level-table-look-a-like {
  .__table-container {
    margin: 0 !important;
    height: 50vh;
  }
}

.w-expansion-panel-header {
  box-shadow: $expansion-panel-box-shadow;
  border-style: none;
  padding: $grid-gutter;
  width: 100%;
  background-color: var(--v-work_card-base);

  //margin-top: 0.125rem;
}

.w-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
  $content-padding: 0;
  background-color: var(--v-work_card-base);
  padding: 0;
}
</style>
